// Menu Financeiro (pedido 2026-09-28) — só admin/funcionário. Faturas dos
// clientes vêm ao vivo do Asaas; despesas/entradas são lançamentos da
// empresa (backend src/finance/). Cada aba/botão respeita as ações da
// categoria "financeiro" do funcionário (admin sempre tem tudo) — o backend
// aplica a mesma regra, esconder aqui é só pra não mostrar o que daria 403.
import { createFileRoute } from "@tanstack/react-router";
import { GpsInlineLoader } from "@/components/gps-loader";
import { useMemo, useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Pencil,
  Plus,
  Receipt,
  RefreshCw,
  Repeat,
  Search,
  Trash2,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  useCreateFinanceTransaction,
  useDeleteFinanceTransaction,
  useFinanceAccess,
  useFinanceSummary,
  useFinanceTransactions,
  useUpdateFinanceTransaction,
} from "@/hooks/queries/finance";
import {
  gpsDriveFinanceService,
  type FinanceAccess,
  type FinanceInvoicesSection,
  type FinanceSummary,
  type FinanceRecurrence,
  type FinanceSeriesScope,
  type FinanceTransaction,
  type FinanceTransactionInput,
  type FinanceTransactionType,
} from "@/services/gpsdrive-finance.service";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";

export const Route = createFileRoute("/financeiro")({
  component: FinancePageGate,
  head: () => ({ meta: [{ title: "Financeiro — GPS Drive" }] }),
});

// ---------------------------------------------------------------------------
// Formatação

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const brlCompact = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", notation: "compact", maximumFractionDigits: 1 });
const fmt = (n: number) => brl.format(n);
const fmtCompact = (n: number) => (Math.abs(n) >= 10_000 ? brlCompact.format(n) : brl.format(n));
const fmtDate = (d: string | null) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "—");
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const fmtMonth = (ym: string) => `${MONTHS[Number(ym.slice(5, 7)) - 1]}/${ym.slice(2, 4)}`;

function isoLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

type PresetKey = "mes" | "mes_passado" | "3m" | "6m" | "12m" | "ano" | "custom";
const PRESETS: { key: PresetKey; label: string }[] = [
  { key: "mes", label: "Este mês" },
  { key: "mes_passado", label: "Mês passado" },
  { key: "3m", label: "3 meses" },
  { key: "6m", label: "6 meses" },
  { key: "12m", label: "12 meses" },
  { key: "ano", label: "Este ano" },
  { key: "custom", label: "Personalizado" },
];

function presetRange(key: Exclude<PresetKey, "custom">): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const endOfMonth = (yy: number, mm: number) => new Date(yy, mm + 1, 0);
  switch (key) {
    case "mes":
      return { from: isoLocal(new Date(y, m, 1)), to: isoLocal(endOfMonth(y, m)) };
    case "mes_passado":
      return { from: isoLocal(new Date(y, m - 1, 1)), to: isoLocal(endOfMonth(y, m - 1)) };
    case "3m":
      return { from: isoLocal(new Date(y, m - 2, 1)), to: isoLocal(endOfMonth(y, m)) };
    case "6m":
      return { from: isoLocal(new Date(y, m - 5, 1)), to: isoLocal(endOfMonth(y, m)) };
    case "12m":
      return { from: isoLocal(new Date(y, m - 11, 1)), to: isoLocal(endOfMonth(y, m)) };
    case "ano":
      return { from: isoLocal(new Date(y, 0, 1)), to: isoLocal(new Date(y, 11, 31)) };
  }
}

const EXPENSE_CATEGORIES = [
  "Chips/Linhas",
  "Rastreadores/Equipamentos",
  "Salários",
  "Comissões",
  "Instalação/Técnicos",
  "Aluguel",
  "Combustível",
  "Impostos",
  "Marketing",
  "Software/Servidores",
  "Manutenção",
  "Taxas bancárias",
  "Outros",
];
const INCOME_CATEGORIES = ["Instalação", "Venda de equipamento", "Serviço avulso", "Recebimento fora do Asaas", "Outros"];
const PAYMENT_METHODS = ["PIX", "Boleto", "Cartão", "Dinheiro", "Transferência", "Débito automático"];
const BILLING_TYPE_LABEL: Record<string, string> = {
  PIX: "PIX",
  BOLETO: "Boleto",
  CREDIT_CARD: "Cartão",
  UNDEFINED: "A escolher",
  TRANSFER: "Transferência",
  DEPOSIT: "Depósito",
};

// ---------------------------------------------------------------------------
// Gate

function FinancePageGate() {
  const { data: access, isLoading } = useFinanceAccess();
  if (isLoading) {
    return (
      <GpsInlineLoader className="h-[70vh]" />
    );
  }
  if (!access?.view) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">Sem permissão</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Você não tem acesso ao Financeiro. Peça a um administrador para liberar em Usuários → Editar →
            Permissões → Financeiro.
          </p>
        </div>
      </div>
    );
  }
  return <FinancePage access={access} />;
}

// ---------------------------------------------------------------------------
// Página

type TabKey = "geral" | "abertas" | "devedores" | "lancamentos" | "fluxo";

function FinancePage({ access }: { access: FinanceAccess }) {
  const [preset, setPreset] = useState<PresetKey>("mes");
  const [custom, setCustom] = useState(() => presetRange("mes"));
  const range = preset === "custom" ? custom : presetRange(preset);
  const { data: summary, isLoading, isFetching, error } = useFinanceSummary(range.from, range.to);
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState<TabKey>("geral");
  const [dialog, setDialog] = useState<{ type: FinanceTransactionType; editing: FinanceTransaction | null } | null>(null);

  const tabs: { key: TabKey; label: string; show: boolean }[] = [
    { key: "geral", label: "Visão geral", show: true },
    { key: "abertas", label: "Faturas em aberto", show: access.invoices },
    { key: "devedores", label: "Clientes devendo", show: access.invoices },
    { key: "lancamentos", label: "Despesas e entradas", show: access.expenses },
    { key: "fluxo", label: "Entradas e saídas", show: access.invoices || access.expenses },
  ];

  // "Atualizar" pula o cache de 5 min das faturas no servidor.
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await gpsDriveFinanceService.summary(range.from, range.to, true);
      qc.setQueryData(["finance", "summary", range.from, range.to], fresh);
      qc.invalidateQueries({ queryKey: ["finance", "transactions"] });
    } catch {
      toast.error("Não foi possível atualizar agora");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="fin-root min-h-full bg-muted/30">
      <div className="mx-auto max-w-[1400px] space-y-5 p-4 md:p-6">
        {/* Cabeçalho */}
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-500/15 text-lg">💵</span>
              Financeiro
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Faturamento, recebimentos, despesas e inadimplência da empresa
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {access.create && (
              <>
                <Button variant="outline" onClick={() => setDialog({ type: "income", editing: null })}>
                  <ArrowUpCircle className="h-4 w-4 text-[var(--fin-recebido)]" /> Lançar entrada
                </Button>
                <Button onClick={() => setDialog({ type: "expense", editing: null })}>
                  <Plus className="h-4 w-4" /> Lançar despesa
                </Button>
              </>
            )}
          </div>
        </header>

        {/* Filtros: período numa linha só, acima de tudo */}
        <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-background p-2">
          <CalendarClock className="ml-1 h-4 w-4 text-muted-foreground" />
          <div className="flex flex-wrap gap-1">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPreset(p.key)}
                className={cn(
                  "rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors",
                  preset === p.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="flex items-center gap-1.5 text-xs">
              <input
                type="date"
                value={custom.from}
                onChange={(e) => e.target.value && setCustom((c) => ({ ...c, from: e.target.value }))}
                className="h-8 rounded-md border bg-background px-2"
              />
              até
              <input
                type="date"
                value={custom.to}
                onChange={(e) => e.target.value && setCustom((c) => ({ ...c, to: e.target.value }))}
                className="h-8 rounded-md border bg-background px-2"
              />
            </div>
          )}
          <span className="ml-auto hidden text-xs text-muted-foreground sm:inline">
            {fmtDate(range.from)} – {fmtDate(range.to)}
          </span>
          {summary?.invoices && "sync" in summary.invoices && (
            <span
              className="hidden items-center gap-1 text-xs text-muted-foreground md:inline-flex"
              title={summary.invoices.sync.lastError ? `Último erro: ${summary.invoices.sync.lastError}` : undefined}
            >
              {summary.invoices.sync.running ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" /> Sincronizando faturas…
                </>
              ) : (
                <>
                  {summary.invoices.sync.lastError && <AlertTriangle className="h-3 w-3 text-[var(--fin-warning)]" />}
                  Faturas atualizadas {timeAgo(summary.invoices.sync.syncedAt)}
                </>
              )}
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={cn("h-4 w-4", (refreshing || isFetching) && "animate-spin")} />
            Atualizar
          </Button>
        </div>

        {/* Abas */}
        <div className="flex gap-1 overflow-x-auto border-b">
          {tabs
            .filter((t) => t.show)
            .map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors",
                  tab === t.key
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
                {t.key === "devedores" && summary?.invoices && "clientesDevendo" in summary.invoices && summary.invoices.clientesDevendo > 0 && (
                  <span className="ml-1.5 rounded-full bg-[var(--fin-critical)] px-1.5 py-0.5 text-[10px] text-white">
                    {summary.invoices.clientesDevendo}
                  </span>
                )}
              </button>
            ))}
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : error && !summary ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
            Não foi possível carregar o Financeiro.{" "}
            {error instanceof GpsDriveApiError ? error.message : "Tente novamente em instantes."}
          </div>
        ) : !summary ? null : (
          <>
            {tab === "geral" && <OverviewTab summary={summary} access={access} onGoTo={setTab} />}
            {tab === "abertas" && <OpenInvoicesTab invoices={summary.invoices} />}
            {tab === "devedores" && <DebtorsTab invoices={summary.invoices} />}
            {tab === "lancamentos" && (
              <TransactionsTab
                from={range.from}
                to={range.to}
                access={access}
                onNew={(type) => setDialog({ type, editing: null })}
                onEdit={(t) => setDialog({ type: t.type, editing: t })}
              />
            )}
            {tab === "fluxo" && <CashFlowTab summary={summary} />}
          </>
        )}
      </div>

      {dialog && (
        <TransactionDialog
          type={dialog.type}
          editing={dialog.editing}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blocos visuais

// Esqueleto no formato do painel enquanto os dados chegam (só na 1ª vez
// neste navegador — depois abre direto com o último painel salvo).
function LoadingSkeleton() {
  const block = "animate-pulse rounded-2xl border bg-background";
  return (
    <div className="space-y-5" aria-label="Carregando dados financeiros">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando dados financeiros…
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className={cn(block, "h-[104px] p-4")}>
            <div className="h-3 w-24 rounded bg-muted" />
            <div className="mt-4 h-6 w-32 rounded bg-muted" />
            <div className="mt-2 h-2.5 w-40 rounded bg-muted/70" />
          </div>
        ))}
      </div>
      <div className={cn(block, "h-80 p-4")}>
        <div className="h-3 w-56 rounded bg-muted" />
        <div className="mt-6 flex h-56 items-end gap-3">
          {[40, 65, 50, 80, 60, 90, 70, 55, 75, 45, 85, 62].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-muted" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className={cn(block, "h-56")} />
        ))}
      </div>
    </div>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  hint,
  accent,
  emphasis,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: ReactNode;
  accent: string;
  emphasis?: "critical" | "good";
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border bg-background p-4 shadow-sm">
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: accent }} />
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <span className="grid h-7 w-7 place-items-center rounded-lg" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)` }}>
          <span style={{ color: accent }}>
            <Icon className="h-4 w-4" />
          </span>
        </span>
        {label}
      </div>
      <div
        className={cn(
          "mt-2 text-2xl font-bold tracking-tight tabular-nums",
          emphasis === "critical" && "text-[var(--fin-critical)]",
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function Panel({ title, subtitle, action, children, className }: { title: string; subtitle?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("rounded-2xl border bg-background p-4 shadow-sm", className)}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Notice({ tone, children }: { tone: "info" | "warn"; children: ReactNode }) {
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-xl border p-3 text-sm",
        tone === "warn" ? "border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300" : "bg-muted/50 text-muted-foreground",
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

// Barra de progresso da 1ª cópia das faturas (pedido 2026-09-28: "tem que
// mostrar um processo de carregamento"). Só acontece uma vez por empresa.
function SyncProgressCard({ progress }: { progress: { phase: string; done: number; total: number } | null }) {
  const pct = progress && progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0;
  const n = (v: number) => v.toLocaleString("pt-BR");
  const label =
    progress?.phase === "clientes"
      ? `Copiando clientes: ${n(progress.done)} de ${n(progress.total)}`
      : progress?.phase === "faturas"
        ? `Copiando faturas: ${n(progress.done)} de ${n(progress.total)}`
        : "Preparando a cópia das faturas…";
  return (
    <div className="rounded-2xl border border-sky-500/30 bg-sky-500/10 p-5 text-sky-900 dark:text-sky-200">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Loader2 className="h-4 w-4 animate-spin" />
        Buscando as faturas no Asaas pela primeira vez
      </div>
      <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-sky-500/15">
        <div
          className="h-full rounded-full bg-sky-500 transition-[width] duration-700 ease-out"
          style={{ width: `${progress?.phase === "faturas" ? Math.max(pct, 3) : 3}%` }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span>{label}</span>
        {progress?.phase === "faturas" && <span className="font-semibold tabular-nums">{pct}%</span>}
      </div>
      <p className="mt-2 text-xs opacity-80">
        Isso acontece uma vez só. Depois o Financeiro abre na hora e se atualiza sozinho a cada 10 minutos — pode
        continuar usando o sistema enquanto isso.
      </p>
    </div>
  );
}

function InvoicesUnavailable({ invoices }: { invoices: FinanceInvoicesSection }) {
  if (invoices.configured && invoices.error === null && invoices.pending) {
    return <SyncProgressCard progress={invoices.sync.progress} />;
  }
  if (!invoices.configured) {
    return (
      <Notice tone="info">
        Integração com o Asaas desligada — as faturas dos clientes aparecem aqui assim que ela for ativada em
        Administração → Conta &amp; Integrações.
      </Notice>
    );
  }
  return (
    <Notice tone="warn">
      Não foi possível consultar as faturas no Asaas agora ({"error" in invoices ? invoices.error : ""}). Tente
      “Atualizar” em instantes.
    </Notice>
  );
}

type ReadyInvoices = Extract<FinanceInvoicesSection, { pending: false }>;
function readyInvoices(inv: FinanceInvoicesSection | null): ReadyInvoices | null {
  return inv && inv.configured && inv.error === null && !inv.pending ? inv : null;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "nunca";
  const min = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  return h < 24 ? `há ${h} h` : `há ${Math.round(h / 24)} dias`;
}

const tooltipStyle = {
  borderRadius: 10,
  border: "1px solid hsl(var(--border))",
  background: "hsl(var(--background))",
  color: "hsl(var(--foreground))",
  fontSize: 12,
  boxShadow: "0 8px 24px rgba(0,0,0,.12)",
};
const axisTick = { fontSize: 11, fill: "var(--fin-axis)" };

function MoneyTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={tooltipStyle} className="px-3 py-2">
      {label && <div className="mb-1 font-semibold">{label}</div>}
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}</span>
          <span className="ml-auto pl-3 font-semibold tabular-nums">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visão geral

function OverviewTab({ summary, access, onGoTo }: { summary: FinanceSummary; access: FinanceAccess; onGoTo: (t: TabKey) => void }) {
  const inv = readyInvoices(summary.invoices);
  const tx = summary.transactions;
  const series = summary.series.map((s) => ({ ...s, label: fmtMonth(s.month) }));

  const statusData = inv
    ? [
        { key: "paga", name: "Pagas", color: "var(--fin-good)", ...inv.status.paga },
        { key: "pendente", name: "A vencer", color: "var(--fin-warning)", ...inv.status.pendente },
        { key: "vencida", name: "Vencidas", color: "var(--fin-critical)", ...inv.status.vencida },
        { key: "estornada", name: "Estornadas", color: "var(--fin-neutral)", ...inv.status.estornada },
      ].filter((d) => d.count > 0)
    : [];
  const statusTotal = statusData.reduce((s, d) => s + d.total, 0);

  return (
    <div className="space-y-5">
      {summary.invoices && !inv && <InvoicesUnavailable invoices={summary.invoices} />}

      {/* Indicadores */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {inv && (
          <>
            <Kpi icon={Receipt} label="Faturado no período" value={fmt(inv.faturado)} hint={`${inv.faturadoCount} faturas · ticket médio ${fmt(inv.ticketMedio)}`} accent="var(--fin-faturado)" />
            <Kpi icon={CheckCircle2} label="Recebido no período" value={fmt(inv.recebido)} hint={`${inv.recebidoCount} pagamentos · líquido ${fmt(inv.recebidoLiquido)}`} accent="var(--fin-recebido)" />
            <Kpi icon={Wallet} label="Faturas em aberto" value={fmt(inv.emAberto.total)} hint={`${inv.emAberto.count} faturas · ${fmt(inv.aVencer.total)} a vencer`} accent="var(--fin-warning)" />
            <Kpi icon={AlertTriangle} label="Vencido (total)" value={fmt(inv.vencido.total)} hint={`${inv.vencido.count} faturas vencidas`} accent="var(--fin-critical)" emphasis={inv.vencido.total > 0 ? "critical" : undefined} />
            <Kpi icon={TrendingUp} label="Taxa de inadimplência" value={`${inv.inadimplenciaPeriodo.toLocaleString("pt-BR")}%`} hint="do que venceu no período, ainda não pago" accent="var(--fin-critical)" emphasis={inv.inadimplenciaPeriodo >= 10 ? "critical" : undefined} />
            <Kpi icon={Users} label="Clientes devendo" value={String(inv.clientesDevendo)} hint={`${inv.inadimplenciaClientes.toLocaleString("pt-BR")}% dos clientes com fatura`} accent="var(--fin-critical)" />
          </>
        )}
        {tx && (
          <Kpi icon={ArrowDownCircle} label="Despesas no período" value={fmt(tx.despesas)} hint={`${fmt(tx.despesasAPagar)} a pagar${tx.despesasVencidas > 0 ? ` · ${fmt(tx.despesasVencidas)} atrasadas` : ""}`} accent="var(--fin-despesa)" />
        )}
        <Kpi
          icon={Wallet}
          label="Saldo do período"
          value={fmt(summary.cash.saldo)}
          hint={`Entradas ${fmt(summary.cash.entradas)} · Saídas ${fmt(summary.cash.saidas)}`}
          accent={summary.cash.saldo >= 0 ? "var(--fin-good)" : "var(--fin-critical)"}
          emphasis={summary.cash.saldo < 0 ? "critical" : undefined}
        />
      </div>

      {/* Gráfico principal */}
      {(inv || tx) && (
        <Panel title="Faturado, recebido e despesas por mês" subtitle="Faturado pelo vencimento · recebido pela data do pagamento · despesas pela data do lançamento">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} barGap={2} barCategoryGap="22%" margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--fin-grid)" />
                <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => fmtCompact(v)} width={72} />
                <Tooltip content={<MoneyTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                {inv && <Bar dataKey="faturado" name="Faturado" fill="var(--fin-faturado)" radius={[4, 4, 0, 0]} maxBarSize={28} />}
                {inv && <Bar dataKey="recebido" name="Recebido" fill="var(--fin-recebido)" radius={[4, 4, 0, 0]} maxBarSize={28} />}
                {tx && <Bar dataKey="despesas" name="Despesas" fill="var(--fin-despesa)" radius={[4, 4, 0, 0]} maxBarSize={28} />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        {inv && (
          <Panel title="Situação das faturas do período" subtitle="Pelo vencimento, dentro do período">
            {statusData.length === 0 ? (
              <EmptyChart text="Nenhuma fatura vence neste período" />
            ) : (
              <div className="flex items-center gap-4">
                <div className="relative h-44 w-44 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusData} dataKey="total" nameKey="name" innerRadius="62%" outerRadius="100%" paddingAngle={2} stroke="hsl(var(--background))" strokeWidth={2}>
                        {statusData.map((d) => (
                          <Cell key={d.key} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number, n: string) => [fmt(v), n]} contentStyle={tooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total</div>
                      <div className="text-sm font-bold tabular-nums">{fmtCompact(statusTotal)}</div>
                    </div>
                  </div>
                </div>
                <ul className="min-w-0 flex-1 space-y-2 text-xs">
                  {statusData.map((d) => (
                    <li key={d.key} className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: d.color }} />
                      <span className="flex-1 truncate">
                        {d.name} <span className="text-muted-foreground">({d.count})</span>
                      </span>
                      <span className="font-semibold tabular-nums">{fmtCompact(d.total)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Panel>
        )}

        {inv && (
          <Panel title="Atraso das faturas vencidas" subtitle="Todas as vencidas, por tempo de atraso">
            {inv.vencido.count === 0 ? (
              <EmptyChart text="Nenhuma fatura vencida 🎉" />
            ) : (
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={inv.aging} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="label" tick={axisTick} axisLine={false} tickLine={false} width={74} />
                    <Tooltip
                      cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
                      contentStyle={tooltipStyle}
                      formatter={(v: number, _n, item) => [`${fmt(v)} · ${(item.payload as { count: number }).count} faturas`, "Vencido"]}
                    />
                    <Bar dataKey="total" radius={[0, 4, 4, 0]} maxBarSize={18}>
                      {inv.aging.map((_, i) => (
                        <Cell key={i} fill={`var(--fin-aging-${i + 1})`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>
        )}

        {tx && (
          <Panel title="Despesas por categoria" subtitle="No período">
            {tx.porCategoria.length === 0 ? (
              <EmptyChart text="Nenhuma despesa lançada no período" />
            ) : (
              <ul className="space-y-2.5">
                {tx.porCategoria.slice(0, 7).map((c) => {
                  const pct = tx.despesas > 0 ? (c.total / tx.despesas) * 100 : 0;
                  return (
                    <li key={c.category} className="text-xs">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="truncate">{c.category}</span>
                        <span className="shrink-0 font-semibold tabular-nums">
                          {fmt(c.total)} <span className="font-normal text-muted-foreground">· {pct.toFixed(0)}%</span>
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-[var(--fin-despesa)]" style={{ width: `${Math.max(pct, 2)}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>
        )}
      </div>

      {inv && inv.debtors.length > 0 && (
        <Panel
          title="Maiores devedores"
          subtitle="Clientes com faturas vencidas"
          action={
            <Button variant="ghost" size="sm" onClick={() => onGoTo("devedores")}>
              Ver todos
            </Button>
          }
        >
          <DebtorsTable debtors={inv.debtors.slice(0, 5)} />
        </Panel>
      )}

      {!access.invoices && !access.expenses && (
        <Notice tone="info">Seu acesso ao Financeiro mostra só o saldo geral. Peça ao administrador para liberar mais seções.</Notice>
      )}
    </div>
  );
}

function EmptyChart({ text }: { text: string }) {
  return <div className="grid h-40 place-items-center text-sm text-muted-foreground">{text}</div>;
}

// ---------------------------------------------------------------------------
// Faturas em aberto

function OpenInvoicesTab({ invoices }: { invoices: FinanceInvoicesSection | null }) {
  const inv = readyInvoices(invoices);
  const [filter, setFilter] = useState<"todas" | "vencida" | "pendente">("todas");
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    if (!inv) return [];
    const s = q.trim().toLowerCase();
    return inv.openInvoices.filter(
      (i) => (filter === "todas" || i.status === filter) && (!s || i.clientName.toLowerCase().includes(s) || (i.description ?? "").toLowerCase().includes(s)),
    );
  }, [inv, filter, q]);
  if (!invoices) return null;
  if (!inv) return <InvoicesUnavailable invoices={invoices} />;
  const total = rows.reduce((s, i) => s + i.value, 0);

  return (
    <Panel
      title="Faturas em aberto"
      subtitle={`${rows.length} faturas · ${fmt(total)} — todas as datas, independente do período`}
      action={<SearchBox value={q} onChange={setQ} placeholder="Buscar cliente…" />}
    >
      <div className="mb-3 flex gap-1">
        {([
          ["todas", `Todas (${inv.emAberto.count})`],
          ["vencida", `Vencidas (${inv.vencido.count})`],
          ["pendente", `A vencer (${inv.aVencer.count})`],
        ] as const).map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={cn("rounded-lg px-3 py-1.5 text-xs font-semibold", filter === k ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:text-foreground")}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 font-medium">Cliente</th>
              <th className="py-2 pr-3 font-medium">Vencimento</th>
              <th className="py-2 pr-3 font-medium">Situação</th>
              <th className="py-2 pr-3 font-medium">Forma</th>
              <th className="py-2 pr-3 text-right font-medium">Valor</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 500).map((i) => (
              <tr key={i.id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="max-w-[260px] py-2.5 pr-3">
                  <div className="truncate font-medium">{i.clientName}</div>
                  {i.description && <div className="truncate text-xs text-muted-foreground">{i.description}</div>}
                </td>
                <td className="py-2.5 pr-3 tabular-nums">{fmtDate(i.dueDate)}</td>
                <td className="py-2.5 pr-3">
                  <StatusBadge status={i.status} daysLate={i.daysLate} />
                </td>
                <td className="py-2.5 pr-3 text-muted-foreground">{BILLING_TYPE_LABEL[i.billingType] ?? i.billingType}</td>
                <td className="py-2.5 pr-3 text-right font-semibold tabular-nums">{fmt(i.value)}</td>
                <td className="py-2.5 text-right">
                  {i.invoiceUrl && (
                    <a href={i.invoiceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                      Fatura <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <EmptyChart text="Nenhuma fatura em aberto 🎉" />}
      </div>
    </Panel>
  );
}

function StatusBadge({ status, daysLate }: { status: "pendente" | "vencida"; daysLate: number }) {
  return status === "vencida" ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--fin-critical)]/12 px-2 py-0.5 text-xs font-semibold text-[var(--fin-critical)]" style={{ background: "color-mix(in srgb, var(--fin-critical) 12%, transparent)" }}>
      <AlertTriangle className="h-3 w-3" /> Vencida há {daysLate} {daysLate === 1 ? "dia" : "dias"}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold text-[var(--fin-warning)]" style={{ background: "color-mix(in srgb, var(--fin-warning) 14%, transparent)" }}>
      <CalendarClock className="h-3 w-3" /> A vencer
    </span>
  );
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 w-48 rounded-lg border bg-background pl-8 pr-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clientes devendo

function DebtorsTab({ invoices }: { invoices: FinanceInvoicesSection | null }) {
  const inv = readyInvoices(invoices);
  const [q, setQ] = useState("");
  if (!invoices) return null;
  if (!inv) return <InvoicesUnavailable invoices={invoices} />;
  const s = q.trim().toLowerCase();
  const rows = inv.debtors.filter((d) => !s || d.clientName.toLowerCase().includes(s));
  return (
    <Panel
      title="Clientes devendo"
      subtitle={`${inv.debtors.length} clientes · ${fmt(inv.vencido.total)} vencidos no total`}
      action={<SearchBox value={q} onChange={setQ} placeholder="Buscar cliente…" />}
    >
      {rows.length === 0 ? <EmptyChart text="Nenhum cliente devendo 🎉" /> : <DebtorsTable debtors={rows} />}
    </Panel>
  );
}

function DebtorsTable({ debtors }: { debtors: ReadyInvoices["debtors"] }) {
  const max = Math.max(...debtors.map((d) => d.total), 1);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3 font-medium">Cliente</th>
            <th className="py-2 pr-3 font-medium">Faturas vencidas</th>
            <th className="py-2 pr-3 font-medium">Vencida desde</th>
            <th className="py-2 pr-3 font-medium">Atraso</th>
            <th className="w-[28%] py-2 text-right font-medium">Total devido</th>
          </tr>
        </thead>
        <tbody>
          {debtors.map((d) => (
            <tr key={d.customerId} className="border-b last:border-0 hover:bg-muted/40">
              <td className="max-w-[260px] truncate py-2.5 pr-3 font-medium">{d.clientName}</td>
              <td className="py-2.5 pr-3 tabular-nums">{d.invoices}</td>
              <td className="py-2.5 pr-3 tabular-nums">{fmtDate(d.oldestDueDate)}</td>
              <td className="py-2.5 pr-3">
                <span className={cn("font-semibold tabular-nums", d.daysLate > 30 && "text-[var(--fin-critical)]")}>{d.daysLate} dias</span>
              </td>
              <td className="py-2.5">
                <div className="flex items-center justify-end gap-2">
                  <div className="hidden h-1.5 flex-1 overflow-hidden rounded-full bg-muted sm:block">
                    <div className="ml-auto h-full rounded-full bg-[var(--fin-critical)]" style={{ width: `${(d.total / max) * 100}%` }} />
                  </div>
                  <span className="font-semibold tabular-nums">{fmt(d.total)}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Despesas e entradas (lançamentos)

function TransactionsTab({
  from,
  to,
  access,
  onNew,
  onEdit,
}: {
  from: string;
  to: string;
  access: FinanceAccess;
  onNew: (type: FinanceTransactionType) => void;
  onEdit: (t: FinanceTransaction) => void;
}) {
  const [type, setType] = useState<FinanceTransactionType | undefined>(undefined);
  const [q, setQ] = useState("");
  const { data: rows = [], isLoading } = useFinanceTransactions(from, to, type);
  const update = useUpdateFinanceTransaction();
  const remove = useDeleteFinanceTransaction();
  const s = q.trim().toLowerCase();
  const visible = rows.filter((r) => !s || r.description.toLowerCase().includes(s) || r.category.toLowerCase().includes(s));
  const totals = visible.reduce(
    (acc, r) => {
      if (r.type === "expense") acc.saidas += r.amount;
      else acc.entradas += r.amount;
      if (!r.paid && r.type === "expense") acc.aPagar += r.amount;
      return acc;
    },
    { entradas: 0, saidas: 0, aPagar: 0 },
  );
  const today = isoLocal(new Date());

  const togglePaid = (r: FinanceTransaction) => {
    update.mutate(
      { id: r.id, input: { paid: !r.paid, paidAt: r.paid ? null : today } },
      { onError: () => toast.error("Não foi possível atualizar o lançamento") },
    );
  };
  // Parcelada/fixa: pergunta se exclui só esta, esta e as próximas ou todas.
  const [deleting, setDeleting] = useState<FinanceTransaction | null>(null);
  const handleDelete = (r: FinanceTransaction) => {
    if (r.series) return setDeleting(r);
    if (!confirm(`Excluir "${r.description}"?`)) return;
    remove.mutate({ id: r.id }, { onError: () => toast.error("Não foi possível excluir") });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi icon={ArrowDownCircle} label="Despesas (lista)" value={fmt(totals.saidas)} accent="var(--fin-despesa)" />
        <Kpi icon={ArrowUpCircle} label="Entradas manuais (lista)" value={fmt(totals.entradas)} accent="var(--fin-recebido)" />
        <Kpi icon={CalendarClock} label="Despesas a pagar (lista)" value={fmt(totals.aPagar)} accent="var(--fin-warning)" />
      </div>
      <Panel
        title="Despesas e entradas lançadas"
        subtitle="Pela data de vencimento/competência, dentro do período"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={q} onChange={setQ} placeholder="Buscar descrição…" />
            {access.create && (
              <Button size="sm" onClick={() => onNew("expense")}>
                <Plus className="h-4 w-4" /> Despesa
              </Button>
            )}
          </div>
        }
      >
        <div className="mb-3 flex gap-1">
          {([
            [undefined, "Tudo"],
            ["expense", "Despesas"],
            ["income", "Entradas"],
          ] as const).map(([k, l]) => (
            <button
              key={l}
              type="button"
              onClick={() => setType(k)}
              className={cn("rounded-lg px-3 py-1.5 text-xs font-semibold", type === k ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:text-foreground")}
            >
              {l}
            </button>
          ))}
        </div>
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : visible.length === 0 ? (
          <div className="grid h-40 place-items-center text-center text-sm text-muted-foreground">
            <div>
              Nenhum lançamento neste período.
              {access.create && (
                <div className="mt-2">
                  <Button variant="outline" size="sm" onClick={() => onNew("expense")}>
                    <Plus className="h-4 w-4" /> Lançar a primeira despesa
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Descrição</th>
                  <th className="py-2 pr-3 font-medium">Categoria</th>
                  <th className="py-2 pr-3 font-medium">Data</th>
                  <th className="py-2 pr-3 font-medium">Situação</th>
                  <th className="py-2 pr-3 text-right font-medium">Valor</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const late = r.type === "expense" && !r.paid && r.date < today;
                  return (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="max-w-[280px] py-2.5 pr-3">
                        <div className="flex items-center gap-2">
                          {r.type === "expense" ? (
                            <ArrowDownCircle className="h-4 w-4 shrink-0 text-[var(--fin-despesa)]" aria-label="Despesa" />
                          ) : (
                            <ArrowUpCircle className="h-4 w-4 shrink-0 text-[var(--fin-recebido)]" aria-label="Entrada" />
                          )}
                          <span className="truncate font-medium">{r.description}</span>
                          {r.series && <SeriesBadge series={r.series} />}
                        </div>
                        {r.notes && <div className="ml-6 truncate text-xs text-muted-foreground">{r.notes}</div>}
                      </td>
                      <td className="py-2.5 pr-3 text-muted-foreground">{r.category}</td>
                      <td className="py-2.5 pr-3 tabular-nums">{fmtDate(r.date)}</td>
                      <td className="py-2.5 pr-3">
                        <button
                          type="button"
                          disabled={!access.edit || update.isPending}
                          onClick={() => togglePaid(r)}
                          title={access.edit ? (r.paid ? "Marcar como não pago" : "Marcar como pago hoje") : undefined}
                          className="rounded-full disabled:cursor-default"
                        >
                          {r.paid ? (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold text-[var(--fin-good)]" style={{ background: "color-mix(in srgb, var(--fin-good) 12%, transparent)" }}>
                              <CheckCircle2 className="h-3 w-3" /> {r.type === "expense" ? "Pago" : "Recebido"} {fmtDate(r.paidAt)}
                            </span>
                          ) : (
                            <span
                              className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold", late ? "text-[var(--fin-critical)]" : "text-[var(--fin-warning)]")}
                              style={{ background: `color-mix(in srgb, var(${late ? "--fin-critical" : "--fin-warning"}) 12%, transparent)` }}
                            >
                              {late ? <AlertTriangle className="h-3 w-3" /> : <CalendarClock className="h-3 w-3" />}
                              {late ? "Atrasado" : r.type === "expense" ? "A pagar" : "A receber"}
                            </span>
                          )}
                        </button>
                      </td>
                      <td className={cn("py-2.5 pr-3 text-right font-semibold tabular-nums", r.type === "income" && "text-[var(--fin-recebido)]")}>
                        {r.type === "expense" ? "−" : "+"} {fmt(r.amount)}
                      </td>
                      <td className="py-2.5 text-right">
                        <div className="inline-flex gap-1">
                          {access.edit && (
                            <button type="button" onClick={() => onEdit(r)} className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Editar">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {access.delete && (
                            <button type="button" onClick={() => handleDelete(r)} className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label="Excluir">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {deleting && (
        <SeriesScopeDialog
          title="Excluir lançamento repetido"
          description={`"${deleting.description}" faz parte de ${deleting.series?.kind === "installment" ? "um parcelamento" : "uma despesa fixa mensal"}. O que você quer excluir?`}
          confirmLabel="Excluir"
          destructive
          busy={remove.isPending}
          allowFuture={(deleting.series?.index ?? 1) > 1}
          onClose={() => setDeleting(null)}
          onConfirm={(scope) =>
            remove.mutate(
              { id: deleting.id, scope },
              { onSuccess: () => setDeleting(null), onError: () => toast.error("Não foi possível excluir") },
            )
          }
        />
      )}
    </div>
  );
}

function SeriesBadge({ series }: { series: NonNullable<FinanceTransaction["series"]> }) {
  const label = series.kind === "installment" ? `${series.index}/${series.count}` : "Fixa";
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground"
      title={series.kind === "installment" ? `Parcela ${series.index} de ${series.count}` : series.endDate ? `Despesa fixa mensal até ${fmtDate(series.endDate)}` : "Despesa fixa mensal"}
    >
      <Repeat className="h-2.5 w-2.5" /> {label}
    </span>
  );
}

const SCOPE_OPTIONS: { key: FinanceSeriesScope; label: string }[] = [
  { key: "one", label: "Só este lançamento" },
  { key: "future", label: "Este e os próximos" },
  { key: "all", label: "Todos da série" },
];

function SeriesScopeDialog({
  title,
  description,
  confirmLabel,
  destructive,
  busy,
  allowFuture,
  onClose,
  onConfirm,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  busy: boolean;
  allowFuture: boolean;
  onClose: () => void;
  onConfirm: (scope: FinanceSeriesScope) => void;
}) {
  const [scope, setScope] = useState<FinanceSeriesScope>("one");
  // Na 1ª da série "este e os próximos" é o mesmo que "todos".
  const options = SCOPE_OPTIONS.filter((o) => allowFuture || o.key !== "future");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{description}</p>
        <ScopePicker value={scope} onChange={setScope} options={options} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant={destructive ? "destructive" : "default"} onClick={() => onConfirm(scope)} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScopePicker({
  value,
  onChange,
  options,
}: {
  value: FinanceSeriesScope;
  onChange: (s: FinanceSeriesScope) => void;
  options: { key: FinanceSeriesScope; label: string }[];
}) {
  return (
    <div className="space-y-1.5">
      {options.map((o) => (
        <label key={o.key} className={cn("flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm", value === o.key && "border-foreground bg-muted")}>
          <input type="radio" checked={value === o.key} onChange={() => onChange(o.key)} className="h-4 w-4 accent-foreground" />
          {o.label}
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entradas e saídas (fluxo de caixa)

function CashFlowTab({ summary }: { summary: FinanceSummary }) {
  let acc = 0;
  const rows = summary.series.map((s) => {
    acc += s.saldo;
    return { ...s, label: fmtMonth(s.month), acumulado: Math.round(acc * 100) / 100 };
  });
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi icon={ArrowUpCircle} label="Entradas no período" value={fmt(summary.cash.entradas)} hint="Faturas recebidas + entradas manuais pagas" accent="var(--fin-recebido)" />
        <Kpi icon={ArrowDownCircle} label="Saídas no período" value={fmt(summary.cash.saidas)} hint="Despesas pagas" accent="var(--fin-despesa)" />
        <Kpi icon={Wallet} label="Saldo no período" value={fmt(summary.cash.saldo)} accent={summary.cash.saldo >= 0 ? "var(--fin-good)" : "var(--fin-critical)"} emphasis={summary.cash.saldo < 0 ? "critical" : undefined} />
      </div>
      <Panel title="Entradas × saídas por mês" subtitle="Pelo dia em que o dinheiro entrou ou saiu">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} barGap={2} barCategoryGap="26%" margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="var(--fin-grid)" />
              <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => fmtCompact(v)} width={72} />
              <Tooltip content={<MoneyTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="entradas" name="Entradas" fill="var(--fin-recebido)" radius={[4, 4, 0, 0]} maxBarSize={32} />
              <Bar dataKey="saidas" name="Saídas" fill="var(--fin-despesa)" radius={[4, 4, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title="Resultado mês a mês">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Mês</th>
                <th className="py-2 pr-3 text-right font-medium">Faturado</th>
                <th className="py-2 pr-3 text-right font-medium">Entradas</th>
                <th className="py-2 pr-3 text-right font-medium">Saídas</th>
                <th className="py-2 pr-3 text-right font-medium">Saldo do mês</th>
                <th className="py-2 text-right font-medium">Acumulado</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.month} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2.5 pr-3 font-medium capitalize">{r.label}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-muted-foreground">{fmt(r.faturado)}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{fmt(r.entradas)}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{fmt(r.saidas)}</td>
                  <td className={cn("py-2.5 pr-3 text-right font-semibold tabular-nums", r.saldo < 0 && "text-[var(--fin-critical)]")}>{fmt(r.saldo)}</td>
                  <td className={cn("py-2.5 text-right tabular-nums", r.acumulado < 0 && "text-[var(--fin-critical)]")}>{fmt(r.acumulado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lançar / editar

function TransactionDialog({
  type: initialType,
  editing,
  onClose,
}: {
  type: FinanceTransactionType;
  editing: FinanceTransaction | null;
  onClose: () => void;
}) {
  const today = isoLocal(new Date());
  const [type, setType] = useState<FinanceTransactionType>(editing?.type ?? initialType);
  const [description, setDescription] = useState(editing?.description ?? "");
  const [category, setCategory] = useState(editing?.category ?? "");
  const [amount, setAmount] = useState(editing ? editing.amount.toFixed(2).replace(".", ",") : "");
  const [date, setDate] = useState(editing?.date ?? today);
  const [paid, setPaid] = useState(editing?.paid ?? false);
  const [paidAt, setPaidAt] = useState(editing?.paidAt ?? today);
  const [paymentMethod, setPaymentMethod] = useState(editing?.paymentMethod ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  // Repetição (só ao lançar): única, parcelada ou fixa todo mês.
  const [repeat, setRepeat] = useState<"none" | "installment" | "fixed">("none");
  const [installments, setInstallments] = useState("2");
  const [amountIs, setAmountIs] = useState<"total" | "installment">("total");
  const [hasEnd, setHasEnd] = useState(false);
  const [endDate, setEndDate] = useState("");
  // Editando parte de uma série: aplica a só esta / próximas / todas.
  const [scope, setScope] = useState<FinanceSeriesScope>("one");
  const [error, setError] = useState<string | null>(null);
  const create = useCreateFinanceTransaction();
  const update = useUpdateFinanceTransaction();
  const saving = create.isPending || update.isPending;
  const categories = type === "expense" ? EXPENSE_CATEGORIES : INCOME_CATEGORIES;

  const handleSave = () => {
    setError(null);
    const value = Number(amount.replace(/\./g, "").replace(",", "."));
    if (!description.trim()) return setError("Informe a descrição.");
    if (!category.trim()) return setError("Escolha ou digite a categoria.");
    if (!Number.isFinite(value) || value <= 0) return setError("Informe um valor maior que zero.");
    const input: FinanceTransactionInput = {
      type,
      description: description.trim(),
      category: category.trim(),
      amount: Math.round(value * 100) / 100,
      date,
      paid,
      paidAt: paid ? paidAt || today : null,
      paymentMethod: paymentMethod.trim() || null,
      notes: notes.trim() || null,
    };
    let recurrence: FinanceRecurrence | null = null;
    if (!editing && repeat === "installment") {
      const n = Number(installments);
      if (!Number.isInteger(n) || n < 2 || n > 120) return setError("Informe de 2 a 120 parcelas.");
      recurrence = { kind: "installment", count: n, amountIs };
    } else if (!editing && repeat === "fixed") {
      if (hasEnd && !endDate) return setError("Informe até quando a despesa se repete.");
      if (hasEnd && endDate < date) return setError("A data final precisa ser depois do 1º vencimento.");
      recurrence = { kind: "fixed", endDate: hasEnd ? endDate : null };
    }
    const onError = (err: unknown) => setError(err instanceof GpsDriveApiError ? err.message : "Não foi possível salvar.");
    if (editing) {
      // Nos outros lançamentos da série só vão os campos comuns — situação
      // de pagamento continua individual (o backend já ignora nos outros).
      update.mutate({ id: editing.id, input, scope: editing.series ? scope : "one" }, { onSuccess: onClose, onError });
    } else create.mutate({ input, recurrence }, { onSuccess: onClose, onError });
  };

  const field = "h-10 w-full rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing
              ? editing.series
                ? editing.series.kind === "installment"
                  ? `Editar parcela ${editing.series.index}/${editing.series.count}`
                  : "Editar despesa fixa"
                : "Editar lançamento"
              : type === "expense"
                ? "Lançar despesa"
                : "Lançar entrada"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {([
              ["expense", "Despesa (saída)", ArrowDownCircle, "var(--fin-despesa)"],
              ["income", "Entrada", ArrowUpCircle, "var(--fin-recebido)"],
            ] as const).map(([k, l, Icon, color]) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setType(k);
                  setCategory("");
                }}
                className={cn(
                  "flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors",
                  type === k ? "border-foreground bg-muted" : "text-muted-foreground hover:bg-muted/50",
                )}
              >
                <span style={{ color }}>
                  <Icon className="h-4 w-4" />
                </span>
                {l}
              </button>
            ))}
          </div>

          <label className="block space-y-1">
            <span className="text-xs font-semibold">Descrição</span>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={type === "expense" ? "Ex.: Chips Vivo — setembro" : "Ex.: Instalação avulsa"} className={field} autoFocus />
          </label>

          <div className="space-y-1">
            <span className="text-xs font-semibold">Categoria</span>
            <div className="flex flex-wrap gap-1.5">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-xs transition-colors",
                    category === c ? "border-foreground bg-foreground text-background" : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
            <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="…ou digite outra" className={cn(field, "mt-1.5 h-9")} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-xs font-semibold">
                {!editing && repeat === "installment" ? (amountIs === "total" ? "Valor total (R$)" : "Valor da parcela (R$)") : !editing && repeat === "fixed" ? "Valor mensal (R$)" : "Valor (R$)"}
              </span>
              <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))} inputMode="decimal" placeholder="0,00" className={cn(field, "tabular-nums")} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">{!editing && repeat !== "none" ? "1º vencimento" : type === "expense" ? "Vencimento" : "Data"}</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={field} />
            </label>
          </div>

          {!editing && (
            <div className="space-y-2 rounded-lg border p-3">
              <span className="text-xs font-semibold">Repetição</span>
              <div className="grid grid-cols-3 gap-1.5">
                {([
                  ["none", "Única"],
                  ["installment", "Parcelada"],
                  ["fixed", "Fixa mensal"],
                ] as const).map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setRepeat(k)}
                    className={cn(
                      "rounded-lg border px-2 py-2 text-xs font-semibold transition-colors",
                      repeat === k ? "border-foreground bg-foreground text-background" : "text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>
              {repeat === "installment" && (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <label className="block space-y-1">
                    <span className="text-xs font-semibold">Nº de parcelas</span>
                    <input value={installments} onChange={(e) => setInstallments(e.target.value.replace(/\D/g, "").slice(0, 3))} inputMode="numeric" className={cn(field, "tabular-nums")} />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-xs font-semibold">O valor informado é</span>
                    <select value={amountIs} onChange={(e) => setAmountIs(e.target.value as "total" | "installment")} className={field}>
                      <option value="total">O total da compra</option>
                      <option value="installment">De cada parcela</option>
                    </select>
                  </label>
                  <InstallmentPreview amount={amount} count={installments} amountIs={amountIs} date={date} />
                </div>
              )}
              {repeat === "fixed" && (
                <div className="space-y-2 pt-1">
                  <p className="text-xs text-muted-foreground">
                    Lança todo mês no dia {Number(date.slice(8, 10)) || "—"}. Cada mês é pago separado.
                  </p>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="checkbox" checked={hasEnd} onChange={(e) => setHasEnd(e.target.checked)} className="h-4 w-4 accent-foreground" />
                    Tem data para terminar
                  </label>
                  {hasEnd ? (
                    <label className="block space-y-1">
                      <span className="text-xs font-semibold">Repetir até</span>
                      <input type="date" value={endDate} min={date} onChange={(e) => setEndDate(e.target.value)} className={field} />
                    </label>
                  ) : (
                    <p className="text-xs text-muted-foreground">Sem data final: continua todo mês até você excluir "este e os próximos".</p>
                  )}
                </div>
              )}
            </div>
          )}

          {editing?.series && (
            <div className="space-y-1.5 rounded-lg border p-3">
              <span className="text-xs font-semibold">Aplicar alterações em</span>
              <ScopePicker value={scope} onChange={setScope} options={SCOPE_OPTIONS.filter((o) => editing.series!.index > 1 || o.key !== "future")} />
              {scope !== "one" && <p className="text-xs text-muted-foreground">Descrição, categoria, valor, forma e observações vão para os outros lançamentos. Pagamento continua individual.</p>}
            </div>
          )}

          <div className="rounded-lg border p-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} className="h-4 w-4 accent-emerald-600" />
              {type === "expense" ? "Já foi paga" : "Já foi recebida"}
              {!editing && repeat !== "none" && <span className="font-normal text-muted-foreground">(só a 1ª)</span>}
            </label>
            {paid && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <label className="block space-y-1">
                  <span className="text-xs font-semibold">{type === "expense" ? "Pago em" : "Recebido em"}</span>
                  <input type="date" value={paidAt ?? today} onChange={(e) => setPaidAt(e.target.value)} className={field} />
                </label>
                <label className="block space-y-1">
                  <span className="text-xs font-semibold">Forma</span>
                  <input list="fin-payment-methods" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} placeholder="PIX, Boleto…" className={field} />
                  <datalist id="fin-payment-methods">
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </label>
              </div>
            )}
          </div>

          <label className="block space-y-1">
            <span className="text-xs font-semibold">Observações (opcional)</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </label>

          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? "Salvar" : "Lançar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InstallmentPreview({ amount, count, amountIs, date }: { amount: string; count: string; amountIs: "total" | "installment"; date: string }) {
  const value = Number(amount.replace(/\./g, "").replace(",", "."));
  const n = Number(count);
  if (!Number.isFinite(value) || value <= 0 || !Number.isInteger(n) || n < 2) return null;
  const cents = Math.round(value * 100);
  const per = amountIs === "total" ? Math.floor(cents / n) : cents;
  const first = amountIs === "total" ? cents - per * (n - 1) : cents;
  const total = amountIs === "total" ? cents : cents * n;
  // Última parcela: mesmo dia, n-1 meses depois (dia 31 → último do mês).
  const [y, m, d] = date.split("-").map(Number);
  const lastMonth = new Date(Date.UTC(y, m - 1 + n - 1, 1));
  const lastDay = Math.min(d, new Date(Date.UTC(lastMonth.getUTCFullYear(), lastMonth.getUTCMonth() + 1, 0)).getUTCDate());
  const last = isoLocal(new Date(lastMonth.getUTCFullYear(), lastMonth.getUTCMonth(), lastDay));
  return (
    <p className="col-span-2 text-xs text-muted-foreground">
      {n}x de <b className="text-foreground">{fmt(per / 100)}</b>
      {first !== per && <> (1ª de {fmt(first / 100)})</>} · total {fmt(total / 100)} · última em {fmtDate(last)}
    </p>
  );
}
